import {summarizeNativeValue} from './nativeValueSummary';
import {createPatternMatcher} from './patternMatcher';

export interface Redactor {
    /**
     * Traverses the object and replaces all values of the keys that are in
     * provided `keysToRedact` with respective replacement and return a cloned
     * object.
     *
     * Cyclic references become `[Circular]`. An own `toJSON` method is
     * dropped and a function becomes text such as `[Function name]`, because
     * `JSON.stringify` would otherwise call them and serialize what they
     * return instead of the redacted values.
     *
     * @param obj - The object to redact.
     */
    redactObj(obj: Record<string, unknown>): Record<string, unknown>;
    /**
     * Redact the provided string by replacing all occurrences of the patterns
     * and return a new string with redacted values.
     *
     * The values of SDP `a=ice-pwd` and `a=crypto` attributes are always
     * replaced with `defaultReplaceTo`, up to the end of their line or of the
     * enclosing quoted or escaped text, so SDP can be logged without its ICE
     * password or SDES key.
     *
     * Overlapping occurrences are merged into one span, which is replaced with
     * `defaultReplaceTo` unless a single pattern covers the span exactly, so no
     * part of a matched value remains. Adjacent occurrences are replaced
     * separately.
     *
     * @param text - The string to redact.
     *
     * @example
     *
     * ```typescript
     * const redactor = createRedactor(...);
     * redactor.addPattern('AABBCC', '*****');
     * redactor.addPattern('12345', '[Secret Number]');
     *
     * redactor.redactStr('some secret AABBCC and 12345'); // Output 'some secret ***** and [Secret Number]'
     *
     * redactor.addPattern('AB', '[AB]');
     * redactor.addPattern('BC', '[BC]');
     * redactor.redactStr('xABCx'); // Output 'x' + defaultReplaceTo + 'x'
     * ```
     */
    redactStr(text: string): string;
    /**
     * Add a pattern to the redactor and an optional mask for the redaction.
     *
     * @param pattern - The pattern to add.
     * @param replaceTo - The mask to use for redaction.
     *
     * @example
     *
     * ```typescript
     * const redactor = createRedactor(...);
     * redactor.addPattern('AABBCC', '*****');
     * redactor.addPattern('12345', '[Secret Number]');
     *
     * redactor.redactStr('some secret AABBCC and 12345'); // Output 'some secret ***** and [Secret Number]'
     * ```
     */
    addPattern(pattern: string, replaceTo?: string): void;
    /**
     * Whether a pattern could not be registered because the registry is full,
     * see {@link REDACTION_PATTERN_LIMITS}. Once set it stays set for the
     * lifetime of the redactor: `redactStr` then returns only the default
     * replacement, and callers must withhold diagnostic output rather than
     * rely on patterns that were never registered.
     *
     * @example
     * if (redactor.isRedactionIncomplete()) {
     *     return undefined; // drop the remote item
     * }
     */
    isRedactionIncomplete(): boolean;
}

export interface RedactorOptions {
    defaultReplaceTo: string;
    /**
     * Field names whose values are replaced, each with its replacement. Names
     * match regardless of case, underscores and hyphens, so `accessToken`
     * also covers `access_token` and `ACCESS-TOKEN`.
     */
    keysToRedact: [string, string][];
    /**
     * Replacer function to transform logging Javascript value to JSON
     *
     * @param value - Javascript value
     * @returns Transformed value
     */
    objectReplacer?: (
        value: Record<string, unknown>,
    ) => Record<string, unknown>;
}

/**
 * Fixed bounds on the registered patterns of one redactor; not configurable.
 * A registration that would exceed them is rejected and latches
 * {@link Redactor.isRedactionIncomplete}.
 */
export const REDACTION_PATTERN_LIMITS = Object.freeze({
    /** Distinct registered patterns. */
    maxPatterns: 4096,
    /** UTF-8 bytes of all distinct registered patterns. */
    maxPatternBytes: 1024 * 1024,
});

type Brand<DataType> = string & {__dataType: DataType};
type SecretPattern = Brand<'SecretPattern'>;
type SecretMask = Brand<'SecretMask'>;

const isBrandedString = <DataType>(s: unknown): s is Brand<DataType> => {
    if (typeof s === 'string' && s) {
        return true;
    }
    return false;
};

function assertBrandedString<DataType>(
    s: unknown,
): asserts s is Brand<DataType> {
    if (!isBrandedString<DataType>(s)) {
        throw new Error(`Expected a proper branded string: ${s}`, {cause: s});
    }
}

function toBrandedString<DataType>(s: unknown) {
    assertBrandedString<DataType>(s);
    return s;
}

/**
 * The value of an SDP `a=ice-pwd` (RFC 8839) or `a=crypto` (RFC 4568)
 * attribute, up to the end of its line, or to a backslash or quote when the
 * SDP is embedded in escaped or quoted text. A malformed value is masked as a
 * whole rather than in part.
 */
const SDP_CREDENTIAL = /(a=(?:ice-pwd|crypto):)[^\r\n\\"]*/gi;

/**
 * The form in which field names are compared: lower case, without
 * underscores or hyphens.
 *
 * @example
 * normalizeKey('ACCESS_TOKEN'); // 'accesstoken'
 */
const normalizeKey = (key: string) => key.replace(/[-_]/g, '').toLowerCase();

/**
 * A function as text. Its name is read only from its own data property, so no
 * getter runs; the function itself is never called.
 *
 * @example
 * describeFunction(function retry() {}); // '[Function retry]'
 */
const describeFunction = (value: object): string => {
    const name: unknown = Object.getOwnPropertyDescriptor(value, 'name')?.value;
    return typeof name === 'string' && name
        ? `[Function ${name}]`
        : '[Function]';
};

function defaultObjectReplacer(value: Record<string, unknown>) {
    const summary = summarizeNativeValue(value);
    if (summary) {
        return summary;
    }
    if (value instanceof MediaStream) {
        return {
            __type: 'MediaStream',
            id: value.id,
            active: value.active,
        };
    } else if (value instanceof MediaStreamTrack) {
        return {
            __type: 'MediaStreamTrack',
            kind: value.kind,
            id: value.id,
            enabled: value.enabled,
            muted: value.muted,
            readyState: value.readyState,
        };
    } else if (value instanceof Error) {
        return {
            name: value.toString(),
            message: value.message,
        };
    } else if (value instanceof AnalyserNode) {
        return {
            __type: 'AnalyserNode',
            frequencyBinCount: value.frequencyBinCount,
            fftSize: value.fftSize,
            minDecibels: value.minDecibels,
            maxDecibels: value.maxDecibels,
            smoothingTimeConstant: value.smoothingTimeConstant,
        };
    } else if (value instanceof AudioContext) {
        return {
            __type: 'AudioContext',
            audioWorklet: value.audioWorklet,
            state: value.state,
            sampleRate: value.sampleRate,
        };
    } else if (value instanceof MediaStreamAudioSourceNode) {
        return {
            __type: 'MediaStreamAudioSourceNode',
            mediaStream: value.mediaStream,
        };
    } else if (value instanceof MediaStreamAudioDestinationNode) {
        return {
            __type: 'MediaStreamAudioDestinationNode',
            stream: value.stream,
        };
    } else if (value instanceof ChannelMergerNode) {
        return {__type: 'ChannelMergerNode'};
    } else if (value instanceof GainNode) {
        return {__type: 'GainNode', gain: value.gain};
    } else if (value instanceof MediaElementAudioSourceNode) {
        return {
            __type: 'MediaElementAudioSourceNode',
            mediaElement: value.mediaElement,
        };
    } else if (
        'AudioWorkletNode' in window &&
        value instanceof AudioWorkletNode
    ) {
        return {
            __type: 'AudioWorkletNode',
            parameters: value.parameters,
        };
    } else if (value instanceof ChannelSplitterNode) {
        return {
            __type: 'ChannelSplitterNode',
        };
    } else if (value instanceof DelayNode) {
        return {
            __type: 'DelayNode',
            delayTime: value.delayTime,
        };
    } else if (value instanceof RTCRtpTransceiver) {
        return {
            __type: 'RTCRtpTransceiver',
            currentDirection: value.currentDirection,
            direction: value.direction,
            mid: value.mid,
            sender: value.sender,
            receiver: value.receiver,
        };
    } else if (value instanceof RTCRtpSender) {
        return {
            __type: 'RTCRtpSender',
            track: value.track,
        };
    } else if (value instanceof RTCRtpReceiver) {
        return {
            __type: 'RTCRtpReceiver',
            track: value.track,
        };
    } else if (value instanceof RTCSessionDescription) {
        return {
            __type: 'RTCSessionDescription',
            sdp: value.sdp,
            type: value.type,
        };
    }
    return value;
}

export const createRedactor = ({
    defaultReplaceTo,
    keysToRedact,
    objectReplacer = defaultObjectReplacer,
}: RedactorOptions) => {
    const secretKeys = new Map<string, SecretMask>(
        keysToRedact.map(o => [
            normalizeKey(toBrandedString<'SecretKey'>(o[0])),
            toBrandedString<'SecretMask'>(o[1]),
        ]),
    );
    const secretPatternMap = new Map<SecretPattern, SecretMask>();
    const matcher = createPatternMatcher(defaultReplaceTo);
    const textEncoder = new TextEncoder();
    let patternBytes = 0;
    let redactionIncomplete = false;

    // Reuse shared clones, but omit ancestor references so the copy has no cycles.
    const dfs = (
        obj: Record<string, unknown>,
        visited: Map<unknown, unknown>,
        ancestors: Set<object>,
    ) => {
        const old = visited.get(obj);
        if (old) {
            return old as Record<string, unknown>;
        }

        ancestors.add(obj);
        try {
            const clone = {...objectReplacer(obj)};
            // `JSON.stringify` would call it instead of serializing the redacted clone.
            if (typeof clone.toJSON === 'function') {
                Reflect.deleteProperty(clone, 'toJSON');
            }

            visited.set(obj, clone);
            for (const [key, value] of Object.entries(clone)) {
                const mask = secretKeys.get(normalizeKey(key));
                if (mask !== undefined) {
                    clone[key] = mask;
                    continue;
                }
                clone[key] = redactValue(value, visited, ancestors);
            }
            return clone;
        } finally {
            ancestors.delete(obj);
        }
    };

    /**
     * Arrays stay arrays; objects inside them are redacted like any other.
     * Functions become text, because `JSON.stringify` calls a `toJSON` they
     * carry.
     */
    const redactValue = (
        value: unknown,
        visited: Map<unknown, unknown>,
        ancestors: Set<object>,
    ): unknown => {
        if (typeof value === 'function') {
            return describeFunction(value);
        }
        if (typeof value !== 'object' || value === null) {
            return value;
        }
        if (ancestors.has(value)) {
            return '[Circular]';
        }
        if (!Array.isArray(value)) {
            return dfs(value as Record<string, unknown>, visited, ancestors);
        }
        const old = visited.get(value);
        if (old) {
            return old;
        }
        ancestors.add(value);
        try {
            const clone: unknown[] = [];
            visited.set(value, clone);
            for (const item of value) {
                clone.push(redactValue(item, visited, ancestors));
            }
            return clone;
        } finally {
            ancestors.delete(value);
        }
    };

    return Object.freeze<Redactor>({
        redactObj(obj) {
            return dfs(obj, new Map(), new Set());
        },
        redactStr(text) {
            if (redactionIncomplete) {
                return defaultReplaceTo;
            }
            const result = matcher.redact(
                text.replace(
                    SDP_CREDENTIAL,
                    (_value, attribute: string) => attribute + defaultReplaceTo,
                ),
            );
            switch (result.status) {
                case 'redacted':
                    return result.text;
                case 'contains-detect-only':
                    // Return none of the input when a detect-only value is visible.
                    return defaultReplaceTo;
                default: {
                    const unhandled: never = result;
                    return unhandled;
                }
            }
        },
        addPattern(pattern, replaceTo = defaultReplaceTo) {
            const trimmed = toBrandedString<'SecretPattern'>(pattern);
            if (secretPatternMap.has(trimmed)) {
                return;
            }
            const replacement = toBrandedString<'SecretMask'>(replaceTo);
            const bytes = textEncoder.encode(trimmed).byteLength;
            const {maxPatterns, maxPatternBytes} = REDACTION_PATTERN_LIMITS;
            if (
                secretPatternMap.size >= maxPatterns ||
                bytes > maxPatternBytes - patternBytes
            ) {
                redactionIncomplete = true;
                return;
            }
            secretPatternMap.set(trimmed, replacement);
            patternBytes += bytes;
            matcher.add(trimmed, {kind: 'replace', replacement});
        },
        isRedactionIncomplete() {
            return redactionIncomplete;
        },
    });
};
